import { size } from "lodash";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";

import { useMobile } from "@krea/customer-features/hooks";
import Check from "@krea/customer-features/images/svg/check.svg";
import ExternalLink from "@krea/customer-features/images/svg/external-link.svg";
import { AccordionItem } from "@krea/customer-features/shared-components/accordion/AccordionItem";
import Button from "@krea/customer-features/shared-components/button";
import ColouredContainer from "@krea/customer-features/shared-components/coloured-container";
import { Text } from "@krea/customer-features/shared-components/text";
import { IFieldRequest } from "@krea/customer-features/types/fieldRequest";
import { ILenderResponse } from "@krea/customer-features/types/lenderResponse";
import twMerge from "@krea/customer-features/utils/tailwind-utils";
import { Alert } from "@krea/shared/design-system/alerts/Alert";
import i18n from "i18next";

import StepperVertical from "../stepper-vertical";

import PostSelectBidLenderComment from "./PostSelectBidLenderComment";

const PostSelectBidCompletedContactItem = ({
  title,
  value,
}: {
  title: string;
  value: ReactNode;
}) => {
  return (
    <dl className="tw-m-0 tw-mt-2 tw-flex tw-p-0">
      <Text
        htmlTag="dt"
        size="md"
        className="tw-m-0 tw-mr-1 tw-p-0 tw-font-bold tw-text-gray-700"
      >{`${title}:`}</Text>
      <Text htmlTag="dd" size="md" className="tw-m-0 tw-p-0 tw-font-bold">
        {value}
      </Text>
    </dl>
  );
};

interface IStep {
  text: string;
  link?: string;
}

interface ISelectedLenderResponseWithSteps extends ILenderResponse {
  processSteps?: IStep[];
}

export const PostSelectBidCompleted = ({
  selectedLenderResponse,
  signingUrlSideEffect,
  productDetailsAccordion,
}: {
  selectedLenderResponse?: ISelectedLenderResponseWithSteps;
  signingUrlSideEffect: (id?: string) => void;
  productDetailsAccordion?: ReactNode;
}) => {
  const { t } = useTranslation();
  const isMobile = useMobile();

  const lenderCompanyName =
    selectedLenderResponse?.lenderApplication.lenderCompany.name;
  const lenderPhoneNumber =
    selectedLenderResponse?.lenderApplication.lenderCompany
      .customerContactPhoneNumber;
  const lenderEmail =
    selectedLenderResponse?.lenderApplication.lenderCompany
      .customerContactEmail;

  const lenderCompanyPossesiveName =
    i18n.language === "sv"
      ? lenderCompanyName?.endsWith("s")
        ? lenderCompanyName
        : `${lenderCompanyName}s`
      : i18n.language === "en"
        ? `${lenderCompanyName}'s`
        : lenderCompanyName;

  const {
    processSteps,
    fieldRequests = [],
    signingUrl,
  } = selectedLenderResponse ?? {};

  const isCollectedByLenderList: IFieldRequest[] = fieldRequests.filter(
    (r: IFieldRequest) => r.isCollectedByLender,
  );

  const isSigningUrl = signingUrl && signingUrl.length > 0;

  const processStepsDefaultList = isSigningUrl
    ? [
        {
          text: t("postSelect.faq.processSteps.list.withLinkToLender.step1"),
          link: signingUrl,
        },
        { text: t("postSelect.faq.processSteps.list.withLinkToLender.step2") },
        { text: t("postSelect.faq.processSteps.list.withLinkToLender.step3") },
      ]
    : [
        {
          text: t("postSelect.faq.processSteps.list.withoutLinkToLender.step1"),
        },
        {
          text: t("postSelect.faq.processSteps.list.withoutLinkToLender.step2"),
        },
        {
          text: t("postSelect.faq.processSteps.list.withoutLinkToLender.step3"),
        },
      ];

  const processStepsArray =
    size(processSteps) > 0 ? processSteps : processStepsDefaultList;

  const steps = processStepsArray?.map((step: IStep) => {
    return { title: step.text, active: false, link: step.link ?? null };
  });

  return (
    <>
      {signingUrl ? (
        <>
          <ColouredContainer
            header={
              <Alert
                variant="infoAlert"
                className="tw-rounded-b-none tw-rounded-t-md tw-border-none tw-px-5 tw-py-2 has-[*]:tw-my-0"
              >
                <div className="tw-mx-2 tw-flex tw-flex-col">
                  <p className="tw-mb-0">
                    <span className="tw-font-bold ">
                      {t("postSelect.lastStep")}
                    </span>{" "}
                    {t("postSelect.onboardAndProvideInformation", {
                      lenderCompanyName: lenderCompanyPossesiveName,
                    })}
                  </p>
                </div>
              </Alert>
            }
            className="tw-mb-8"
            variant="purple"
          >
            <div className="tw-p-5">
              {productDetailsAccordion}

              {isCollectedByLenderList.length > 0 && (
                <AccordionItem
                  titleStyling="tw-font-bold tw-text-md"
                  title={t("postSelect.isCollectedByLender")}
                  className="tw-mt-5 tw-rounded-md tw-border-gray-300"
                >
                  <div className="tw-m-0 tw-flex tw-flex-col tw-gap-y-5 tw-p-0">
                    {isCollectedByLenderList.map((item, index) => (
                      <div
                        key={index}
                        className="tw-rounded tw-border tw-border-gray-300 tw-p-3"
                      >
                        <Text
                          size="md"
                          className="tw-font-bold"
                        >{`${index + 1}. ${item.displayName}`}</Text>

                        {item.lenderComment && (
                          <PostSelectBidLenderComment
                            comment={item.lenderComment}
                            className="tw-mb-0 tw-mt-3"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </AccordionItem>
              )}
              {(!!lenderPhoneNumber || !!lenderEmail) && (
                <div className="tw-mt-5 tw-rounded-md tw-border tw-border-solid tw-border-gray-300 tw-p-5">
                  <Text className="tw-mb-4 tw-font-bold">
                    {`${t("postSelect.customerContactInfo.heading")} ${
                      lenderCompanyName
                    }:`}
                  </Text>

                  {lenderPhoneNumber && (
                    <PostSelectBidCompletedContactItem
                      title={t("postSelect.customerContactInfo.phone")}
                      value={
                        <a
                          href={`tel:${lenderPhoneNumber.replace(/[^+\d]/g, "")}`}
                        >
                          {lenderPhoneNumber}
                        </a>
                      }
                    />
                  )}

                  {lenderEmail && (
                    <PostSelectBidCompletedContactItem
                      title={t("postSelect.customerContactInfo.email")}
                      value={
                        <a href={`mailto:${lenderEmail}`}>{lenderEmail}</a>
                      }
                    />
                  )}
                </div>
              )}

              {!isMobile ? (
                <a
                  href={signingUrl}
                  onClick={() =>
                    signingUrlSideEffect(selectedLenderResponse?.id)
                  }
                  target="_blank"
                  title={t("postSelect.toast.withLinkToLender.buttonText")}
                  rel="noreferrer"
                >
                  <Button size="lg" className="tw-mt-5 tw-w-full">
                    <div className="tw-flex tw-items-center tw-gap-x-2">
                      {t("postSelect.toast.withLinkToLender.buttonText")}
                      <SVG
                        src={ExternalLink}
                        className="tw-h-[20px] tw-w-auto tw-mt-[2px] tw-fill-white"
                      />
                    </div>
                  </Button>
                </a>
              ) : null}
            </div>
          </ColouredContainer>
        </>
      ) : (
        <ColouredContainer
          header={
            <Alert
              variant="info"
              className="tw-rounded-b-none tw-rounded-t-md tw-border-none tw-px-5 tw-py-2 has-[*]:tw-my-0"
              leftElement={
                <div className="tw-flex tw-h-[17px] tw-w-[17px] tw-items-center tw-justify-center tw-rounded-full tw-bg-mint-500">
                  <SVG src={Check} className="tw-h-auto tw-w-[12px]" />
                </div>
              }
            >
              <div className="tw-ml-2 tw-flex tw-flex-col">
                <p className="tw-mb-0 tw-font-bold">
                  {t("postSelect.allInformationProvided")}
                </p>
                <p className="tw-mb-0">
                  {t("postSelect.lenderWillReviewYourApplication")}
                </p>
              </div>
            </Alert>
          }
          className={twMerge({
            "tw-mb-[50px]": isMobile && isSigningUrl,
            "tw-mb-[35px]": isMobile && !isSigningUrl,
          })}
          variant="mint"
        >
          <div className="tw-p-5">
            {productDetailsAccordion}

            {(!!lenderPhoneNumber || !!lenderEmail) && (
              <div className="tw-mt-5 tw-rounded-md tw-border tw-border-solid tw-border-gray-300 tw-p-5">
                <Text className="tw-mb-4 tw-font-bold">
                  {`${t("postSelect.customerContactInfo.heading")} ${
                    lenderCompanyName
                  }:`}
                </Text>

                {lenderPhoneNumber && (
                  <PostSelectBidCompletedContactItem
                    title={t("postSelect.customerContactInfo.phone")}
                    value={
                      <a
                        href={`tel:${lenderPhoneNumber.replace(/[^+\d]/g, "")}`}
                      >
                        {lenderPhoneNumber}
                      </a>
                    }
                  />
                )}

                {lenderEmail && (
                  <PostSelectBidCompletedContactItem
                    title={t("postSelect.customerContactInfo.email")}
                    value={<a href={`mailto:${lenderEmail}`}>{lenderEmail}</a>}
                  />
                )}
              </div>
            )}
          </div>
        </ColouredContainer>
      )}

      {steps && (
        <AccordionItem
          id="ps-steps"
          className="tw-mt-7 xl:tw-mt-8"
          title={t("postSelect.faq.processSteps.title")}
        >
          <StepperVertical steps={steps} className={""} title={""} />
        </AccordionItem>
      )}
      <AccordionItem
        id="ps-text"
        className={twMerge("tw-mt-4", {
          "tw-mb-[80px]": isSigningUrl && isMobile,
        })}
        title={t("postSelect.faq.explanation.title")}
      >
        <Text>{t("postSelect.faq.explanation.text")}</Text>
      </AccordionItem>

      {isSigningUrl && isMobile && (
        <div
          style={{
            boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.10)",
          }}
          className="tw-fixed tw-bottom-0 tw-left-0 tw-z-[100000] tw-h-[100px] tw-w-full tw-border tw-border-solid tw-border-gray-200 tw-bg-white tw-px-6"
        >
          <a
            href={signingUrl}
            onClick={() => signingUrlSideEffect(selectedLenderResponse?.id)}
            target="_blank"
            title={t("postSelect.toast.withLinkToLender.buttonText")}
            rel="noreferrer"
          >
            <Button
              block
              size="lg"
              tabIndex={0}
              className={
                "tw-mx-auto tw-mt-6 tw-w-full tw-max-w-[590px] tw-text-center md:tw-max-w-[720px] lg:tw-max-w-[750px]"
              }
            >
              <div className="tw-flex tw-items-center tw-gap-x-2">
                {t("postSelect.toast.withLinkToLender.buttonText")}
                <SVG
                  src={ExternalLink}
                  className="tw-h-[20px] tw-w-auto tw-mt-[2px] tw-fill-white"
                />
              </div>
            </Button>
          </a>
        </div>
      )}
    </>
  );
};
